/**
 * Listas — Catálogo maestro de Proveedores, Clientes, Obras y Materiales
 * con códigos estandarizados (PR-0001, CL-T001, OB-T001-0001-RO-00, MT-0001).
 *
 * - Proveedores: CRUD completo + múltiples sucursales
 * - Clientes / Obras: solo lectura (gestión desde Presupuestos)
 * - Materiales: CRUD individual + importación masiva + precio del Libro de Compras
 *
 * Este componente se renderiza como la PRIMERA pestaña del módulo
 * /compras/materiales (por pedido del equipo Zen).
 */
import { useState, useEffect, useCallback, useRef } from "react";
import { createPortal } from "react-dom";
import { Link } from "react-router-dom";
import axios from "axios";
import {
  Building2, Users, HardHat, Package, Plus, Search, X,
  Pencil, Trash2, Loader2, Copy, Check, Upload, Lock, Info,
  RefreshCw, Download, ChevronDown, ChevronUp, FileText, Database
} from "lucide-react";
import { API } from "../contexts/AuthContext";
import { toast } from "sonner";
import { formatPYG } from "../utils/format";

const SUB_TABS = [
  { id: "proveedores",   label: "Proveedores",   icon: Building2, color: "#F97316" },
  { id: "clientes",      label: "Clientes",       icon: Users,     color: "#2E6DA4" },
  { id: "obras",         label: "Obras",          icon: HardHat,   color: "#8B5CF6" },
  { id: "materiales",    label: "Materiales",     icon: Package,   color: "#10B981" },
  { id: "frentes",       label: "Frentes",        icon: Database,  color: "#EC4899" },
  { id: "presupuestos",  label: "Presupuestos",   icon: FileText,  color: "#6366F1" },
];

// ──────────────────────────────────────────────────────────
// MAIN COMPONENT
// ──────────────────────────────────────────────────────────
export default function ListasTab() {
  const [sub, setSub] = useState("proveedores");
  const [stats, setStats] = useState({ proveedores: 0, clientes: 0, obras: 0, materiales: 0, frentes: 0, presupuestos: 0 });

  const loadStats = useCallback(async () => {
    try {
      const [rListas, rBudgets] = await Promise.allSettled([
        axios.get(`${API}/listas/stats`),
        axios.get(`${API}/budgets?status=aprobado`),
      ]);
      const listaStats = rListas.status === "fulfilled" ? (rListas.value.data || {}) : {};
      const budgetsData = rBudgets.status === "fulfilled" ? (rBudgets.value.data || []) : [];
      setStats({ ...listaStats, presupuestos: Array.isArray(budgetsData) ? budgetsData.length : 0 });
    } catch (_e) {
      // silent
    }
  }, []);

  useEffect(() => { loadStats(); }, [loadStats]);

  return (
    <div className="space-y-4" data-testid="listas-tab">
      {/* Sub-tabs */}
      <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-1.5 flex gap-1 overflow-x-auto">
        {SUB_TABS.map((t) => {
          const Icon = t.icon;
          const active = sub === t.id;
          return (
            <button
              key={t.id}
              onClick={() => setSub(t.id)}
              data-testid={`listas-subtab-${t.id}`}
              className={`flex items-center gap-2 px-4 py-2.5 rounded-xl text-[13px] font-semibold whitespace-nowrap transition-all ${
                active
                  ? "bg-gray-900 text-white shadow-sm"
                  : "text-gray-600 hover:bg-gray-50"
              }`}
            >
              <Icon size={15} style={{ color: active ? t.color : undefined }} />
              <span>{t.label}</span>
              <span
                className={`ml-1 px-1.5 py-0.5 rounded-full text-[10px] font-bold ${
                  active ? "bg-white/20 text-white" : "bg-gray-100 text-gray-500"
                }`}
              >
                {stats[t.id] ?? 0}
              </span>
            </button>
          );
        })}
      </div>

      {/* Section */}
      {sub === "proveedores"  && <ProveedoresSection  onChange={loadStats} />}
      {sub === "clientes"     && <ClientesSection     onChange={loadStats} />}
      {sub === "obras"        && <ObrasSection        onChange={loadStats} />}
      {sub === "materiales"   && <MaterialesSection   onChange={loadStats} />}
      {sub === "frentes"      && <FrentesSection      onChange={loadStats} />}
      {sub === "presupuestos" && <PresupuestosSection />}
    </div>
  );
}

// ──────────────────────────────────────────────────────────
// REUSABLE: CODE BADGE (with copy)
// ──────────────────────────────────────────────────────────
function CodeBadge({ code, color = "#2E6DA4" }) {
  const [copied, setCopied] = useState(false);
  const copy = async (e) => {
    e.stopPropagation();
    try {
      if (navigator.clipboard && window.isSecureContext) {
        await navigator.clipboard.writeText(code);
      } else {
        const ta = document.createElement("textarea");
        ta.value = code; ta.style.position = "fixed"; ta.style.opacity = "0";
        document.body.appendChild(ta); ta.select();
        try { document.execCommand("copy"); } finally { document.body.removeChild(ta); }
      }
      setCopied(true);
      toast.success("Código copiado");
      setTimeout(() => setCopied(false), 1500);
    } catch (_err) {
      toast.error("No se pudo copiar al portapapeles");
    }
  };
  return (
    <button
      onClick={copy}
      className="inline-flex items-center gap-1.5 px-2 py-0.5 rounded-md font-mono text-[11.5px] font-bold tracking-wide transition-all hover:brightness-110"
      style={{ background: `${color}15`, color, border: `1px solid ${color}30` }}
      data-testid={`code-${code}`}
      title="Click para copiar"
    >
      {code}
      {copied ? <Check size={10} /> : <Copy size={10} className="opacity-50" />}
    </button>
  );
}

// ──────────────────────────────────────────────────────────
// REUSABLE: SECTION HEADER + SEARCH
// ──────────────────────────────────────────────────────────
function SectionHeader({ title, subtitle, onNew, search, setSearch, color, extraButtons, readOnly, manageHref }) {
  return (
    <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-4 flex items-center justify-between gap-3 flex-wrap">
      <div>
        <h3 className="text-[17px] font-bold text-gray-900 tracking-tight">{title}</h3>
        <p className="text-[12px] text-gray-500 mt-0.5">{subtitle}</p>
      </div>
      <div className="flex items-center gap-2">
        {readOnly && (
          <span className="flex items-center gap-1 px-2.5 py-1.5 rounded-lg bg-amber-50 border border-amber-200 text-amber-700 text-[11px] font-semibold">
            <Lock size={11} />
            {manageHref ? (
              <>
                Solo lectura —{" "}
                <Link to={manageHref}
                  className="underline decoration-amber-400 underline-offset-2 hover:text-amber-900 inline-flex items-center gap-0.5"
                  data-testid="clientes-gestionar-link">
                  Gestionar desde Presupuestos →
                </Link>
              </>
            ) : (
              "Solo lectura — Gestionar desde Presupuestos"
            )}
          </span>
        )}
        <div className="relative">
          <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
          <input
            type="text"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Buscar..."
            className="pl-9 pr-3 py-2 text-[13px] rounded-xl border border-gray-200 bg-gray-50 focus:bg-white focus:border-gray-400 focus:outline-none w-52"
            data-testid="listas-search"
          />
        </div>
        {extraButtons}
        {!readOnly && onNew && (
          <button
            onClick={onNew}
            className="flex items-center gap-1.5 px-3.5 py-2 rounded-xl text-[13px] font-semibold text-white shadow-sm hover:brightness-110 transition-all"
            style={{ background: color }}
            data-testid="listas-new-btn"
          >
            <Plus size={14} />
            <span>Nuevo</span>
          </button>
        )}
      </div>
    </div>
  );
}

// ──────────────────────────────────────────────────────────
// REUSABLE: EMPTY STATE
// ──────────────────────────────────────────────────────────
function EmptyState({ icon: Icon, label, onNew, color }) {
  return (
    <div className="bg-white rounded-2xl border border-dashed border-gray-200 p-12 text-center">
      <div
        className="w-14 h-14 rounded-2xl mx-auto flex items-center justify-center mb-3"
        style={{ background: `${color}15`, color }}
      >
        <Icon size={22} />
      </div>
      <h4 className="text-[14px] font-semibold text-gray-800">Aún no hay {label.toLowerCase()}</h4>
      <p className="text-[12px] text-gray-500 mt-1">Comenzá creando el primero.</p>
      <button
        onClick={onNew}
        className="mt-4 inline-flex items-center gap-1.5 px-4 py-2 rounded-xl text-[13px] font-semibold text-white"
        style={{ background: color }}
      >
        <Plus size={14} /> Crear {label}
      </button>
    </div>
  );
}

// ──────────────────────────────────────────────────────────
// REUSABLE: MODAL
// ──────────────────────────────────────────────────────────
function Modal({ title, onClose, children, size = "md" }) {
  const w = { sm: "max-w-sm", md: "max-w-lg", lg: "max-w-2xl" }[size];
  return createPortal(
    <div className="fixed inset-0 z-[9999] flex items-center justify-center p-4 bg-black/40 backdrop-blur-sm">
      <div className={`bg-white rounded-2xl shadow-2xl w-full ${w} max-h-[90vh] flex flex-col`}>
        <div className="flex items-center justify-between px-5 py-3.5 border-b border-gray-100">
          <h3 className="text-[15px] font-bold text-gray-900">{title}</h3>
          <button onClick={onClose} className="p-1 rounded-lg hover:bg-gray-100 text-gray-500">
            <X size={18} />
          </button>
        </div>
        <div className="p-5 overflow-y-auto">{children}</div>
      </div>
    </div>,
    document.body
  );
}

// ──────────────────────────────────────────────────────────
// REUSABLE: FORM FIELD
// ──────────────────────────────────────────────────────────
function Field({ label, required, children }) {
  return (
    <div>
      <label className="block text-[11.5px] font-semibold text-gray-700 mb-1 uppercase tracking-wide">
        {label} {required && <span className="text-red-500">*</span>}
      </label>
      {children}
    </div>
  );
}

const inputCls = "w-full px-3 py-2 text-[13px] rounded-xl border border-gray-200 bg-white focus:border-gray-400 focus:outline-none";

// ──────────────────────────────────────────────────────────
// PROVEEDORES
// ──────────────────────────────────────────────────────────
function ProveedoresSection({ onChange }) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [editing, setEditing] = useState(null);
  const [open, setOpen] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await axios.get(`${API}/listas/proveedores`);
      setItems(r.data || []);
    } catch (e) {
      toast.error("Error al cargar proveedores");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const handleSave = async (data) => {
    try {
      if (editing?._id) {
        await axios.put(`${API}/listas/proveedores/${editing._id}`, data);
        toast.success("Proveedor actualizado");
      } else {
        await axios.post(`${API}/listas/proveedores`, data);
        toast.success("Proveedor creado");
      }
      setOpen(false); setEditing(null);
      await load(); onChange?.();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Error al guardar");
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm("¿Eliminar proveedor?")) return;
    try {
      await axios.delete(`${API}/listas/proveedores/${id}`);
      toast.success("Proveedor eliminado");
      await load(); onChange?.();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Error");
    }
  };

  // Helper: get sucursales array from doc (handle both old `sucursal` string and new `sucursales` array)
  const getSucursales = (x) => {
    if (Array.isArray(x.sucursales) && x.sucursales.length > 0) return x.sucursales.filter(Boolean);
    if (x.sucursal) return [x.sucursal];
    return [];
  };

  const filtered = items.filter((x) => {
    const q = search.toLowerCase();
    const sucs = getSucursales(x).join(" ").toLowerCase();
    return !q || [x.proveedor, x.codigo, x.rubro, sucs, x.celular]
      .some((v) => (v || "").toLowerCase().includes(q));
  });

  return (
    <div className="space-y-3">
      <SectionHeader
        title="Lista de Proveedores"
        subtitle={`${items.length} proveedor(es) · Código: PR-XXXX`}
        onNew={() => { setEditing(null); setOpen(true); }}
        search={search} setSearch={setSearch}
        color="#F97316"
      />

      {loading ? (
        <div className="bg-white rounded-2xl p-8 text-center text-gray-400">
          <Loader2 size={20} className="mx-auto animate-spin" />
        </div>
      ) : filtered.length === 0 ? (
        <EmptyState icon={Building2} label="Proveedores" color="#F97316"
          onNew={() => { setEditing(null); setOpen(true); }} />
      ) : (
        <div className="bg-white rounded-2xl border border-gray-100 shadow-sm overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full text-[13px]">
              <thead className="bg-gray-50">
                <tr className="text-left text-[11px] uppercase tracking-wider text-gray-500">
                  <th className="px-4 py-3 font-semibold">Código</th>
                  <th className="px-4 py-3 font-semibold">Proveedor</th>
                  <th className="px-4 py-3 font-semibold">Rubro</th>
                  <th className="px-4 py-3 font-semibold">Sucursales</th>
                  <th className="px-4 py-3 font-semibold">Celular</th>
                  <th className="px-4 py-3 font-semibold text-right">Acciones</th>
                </tr>
              </thead>
              <tbody>
                {filtered.map((x) => {
                  const sucs = getSucursales(x);
                  return (
                    <tr key={x._id} className="border-t border-gray-100 hover:bg-gray-50/60" data-testid={`row-${x.codigo}`}>
                      <td className="px-4 py-2.5"><CodeBadge code={x.codigo} color="#F97316" /></td>
                      <td className="px-4 py-2.5 font-semibold text-gray-900">{x.proveedor}</td>
                      <td className="px-4 py-2.5 text-gray-600">{x.rubro || "—"}</td>
                      <td className="px-4 py-2.5">
                        {sucs.length === 0 ? (
                          <span className="text-gray-400">—</span>
                        ) : (
                          <div className="flex flex-wrap gap-1">
                            {sucs.map((s, i) => (
                              <span key={i} className="inline-block px-2 py-0.5 rounded-md text-[11px] bg-orange-50 text-orange-700 border border-orange-200 font-medium">{s}</span>
                            ))}
                          </div>
                        )}
                      </td>
                      <td className="px-4 py-2.5 text-gray-600">{x.celular || "—"}</td>
                      <td className="px-4 py-2.5 text-right">
                        <div className="inline-flex items-center gap-1">
                          <button onClick={() => { setEditing(x); setOpen(true); }}
                            className="p-1.5 rounded-lg hover:bg-blue-50 text-blue-600" title="Editar">
                            <Pencil size={13} />
                          </button>
                          <button onClick={() => handleDelete(x._id)}
                            className="p-1.5 rounded-lg hover:bg-red-50 text-red-600" title="Eliminar">
                            <Trash2 size={13} />
                          </button>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {open && (
        <ProveedorForm
          initial={editing}
          onCancel={() => { setOpen(false); setEditing(null); }}
          onSave={handleSave}
        />
      )}
    </div>
  );
}

function ProveedorForm({ initial, onCancel, onSave }) {
  // Handle backward compat: old `sucursal` string → new `sucursales` array
  const initSucs = () => {
    if (Array.isArray(initial?.sucursales) && initial.sucursales.length > 0) return initial.sucursales.filter(Boolean);
    if (initial?.sucursal) return [initial.sucursal];
    return [""];
  };
  const initConds = () => {
    if (Array.isArray(initial?.condiciones_pago) && initial.condiciones_pago.length > 0) return initial.condiciones_pago.filter(Boolean);
    return [""];
  };

  const [f, setF] = useState({
    proveedor: initial?.proveedor || "",
    rubro: initial?.rubro || "",
    celular: initial?.celular || "",
    email: initial?.email || "",
    ruc: initial?.ruc || "",
  });
  const [sucursales, setSucursales] = useState(initSucs);
  const [condicionesPago, setCondicionesPago] = useState(initConds);
  const [saving, setSaving] = useState(false);

  const addSucursal = () => setSucursales([...sucursales, ""]);
  const removeSucursal = (i) => setSucursales(sucursales.filter((_, idx) => idx !== i));
  const updateSucursal = (i, val) => setSucursales(sucursales.map((s, idx) => idx === i ? val : s));

  const addCondicion = () => setCondicionesPago([...condicionesPago, ""]);
  const removeCondicion = (i) => setCondicionesPago(condicionesPago.filter((_, idx) => idx !== i));
  const updateCondicion = (i, val) => setCondicionesPago(condicionesPago.map((s, idx) => idx === i ? val : s));

  const submit = async (e) => {
    e.preventDefault();
    if (!f.proveedor.trim()) { toast.error("Nombre obligatorio"); return; }
    setSaving(true);
    await onSave({
      ...f,
      sucursales: sucursales.filter(Boolean),
      condiciones_pago: condicionesPago.filter(Boolean),
    });
    setSaving(false);
  };

  return (
    <Modal title={initial ? `Editar Proveedor · ${initial.codigo}` : "Nuevo Proveedor"} onClose={onCancel} size="lg">
      <form onSubmit={submit} className="space-y-3" data-testid="proveedor-form">
        <Field label="Proveedor" required>
          <input className={inputCls} value={f.proveedor}
            onChange={(e) => setF({ ...f, proveedor: e.target.value })}
            data-testid="input-proveedor" autoFocus />
        </Field>
        <div className="grid grid-cols-2 gap-3">
          <Field label="Rubro">
            <input className={inputCls} value={f.rubro}
              onChange={(e) => setF({ ...f, rubro: e.target.value })}
              placeholder="Eléctricos, Cableado..." />
          </Field>
          <Field label="Celular">
            <input className={inputCls} value={f.celular}
              onChange={(e) => setF({ ...f, celular: e.target.value })}
              placeholder="0981 123 456" />
          </Field>
        </div>
        <div className="grid grid-cols-2 gap-3">
          <Field label="RUC">
            <input className={inputCls} value={f.ruc}
              onChange={(e) => setF({ ...f, ruc: e.target.value })}
              placeholder="80012345-1" />
          </Field>
          <Field label="Email">
            <input type="email" className={inputCls} value={f.email}
              onChange={(e) => setF({ ...f, email: e.target.value })}
              placeholder="contacto@proveedor.com" />
          </Field>
        </div>

        {/* Multiple Sucursales */}
        <div>
          <div className="flex items-center justify-between mb-2">
            <label className="text-[12px] font-semibold text-gray-700 uppercase tracking-wide">Sucursales</label>
            <button type="button" onClick={addSucursal}
              className="flex items-center gap-1 text-[12px] font-semibold text-orange-600 hover:text-orange-700"
              data-testid="add-sucursal-btn">
              <Plus size={12} /> Agregar sucursal
            </button>
          </div>
          <div className="space-y-2">
            {sucursales.map((s, i) => (
              <div key={i} className="flex items-center gap-2">
                <span className="text-[11px] text-gray-400 font-mono w-5">{i + 1}</span>
                <input
                  className={`${inputCls} flex-1`}
                  value={s}
                  onChange={(e) => updateSucursal(i, e.target.value)}
                  placeholder="Ej: Asunción, Villa Morra"
                  data-testid={`sucursal-input-${i}`}
                />
                {sucursales.length > 1 && (
                  <button type="button" onClick={() => removeSucursal(i)}
                    className="p-1 rounded-lg hover:bg-red-50 text-red-400 hover:text-red-600">
                    <X size={13} />
                  </button>
                )}
              </div>
            ))}
          </div>
          <p className="text-[11px] text-gray-400 mt-1.5">Podés agregar todas las sucursales del proveedor.</p>
        </div>

        {/* Condiciones de Pago */}
        <div>
          <div className="flex items-center justify-between mb-2">
            <label className="text-[12px] font-semibold text-gray-700 uppercase tracking-wide">Condiciones de Pago Aceptadas</label>
            <button type="button" onClick={addCondicion}
              className="flex items-center gap-1 text-[12px] font-semibold text-orange-600 hover:text-orange-700"
              data-testid="add-condicion-btn">
              <Plus size={12} /> Agregar condición
            </button>
          </div>
          <div className="space-y-2">
            {condicionesPago.map((c, i) => (
              <div key={i} className="flex items-center gap-2">
                <span className="text-[11px] text-gray-400 font-mono w-5">{i + 1}</span>
                <input
                  className={`${inputCls} flex-1`}
                  value={c}
                  onChange={(e) => updateCondicion(i, e.target.value)}
                  placeholder="Ej: Contado, 30 días, 30/60/90 días, A convenir"
                  data-testid={`condicion-pago-input-${i}`}
                />
                {condicionesPago.length > 1 && (
                  <button type="button" onClick={() => removeCondicion(i)}
                    className="p-1 rounded-lg hover:bg-red-50 text-red-400 hover:text-red-600">
                    <X size={13} />
                  </button>
                )}
              </div>
            ))}
          </div>
          <p className="text-[11px] text-gray-400 mt-1.5">Estas condiciones se desplegarán al generar Órdenes de Compra o Presupuestos Finales.</p>
        </div>

        <div className="flex justify-end gap-2 pt-2">
          <button type="button" onClick={onCancel} className="px-4 py-2 rounded-xl text-[13px] font-semibold text-gray-600 hover:bg-gray-100">Cancelar</button>
          <button type="submit" disabled={saving}
            className="px-4 py-2 rounded-xl text-[13px] font-semibold text-white bg-[#F97316] hover:brightness-110 disabled:opacity-50"
            data-testid="save-proveedor-btn">
            {saving ? <Loader2 size={14} className="animate-spin" /> : "Guardar"}
          </button>
        </div>
      </form>
    </Modal>
  );
}

// ──────────────────────────────────────────────────────────
// CLIENTES
// ──────────────────────────────────────────────────────────
// ──────────────────────────────────────────────────────────
// CLIENTES — Solo lectura (gestionar desde Presupuestos)
// ──────────────────────────────────────────────────────────
function ClientesSection({ onChange }) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await axios.get(`${API}/listas/clientes`);
      setItems(r.data || []);
    } catch (_e) { toast.error("Error al cargar clientes"); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const filtered = items.filter((x) => {
    const q = search.toLowerCase();
    return !q || [x.cliente, x.codigo, x.ruc_ci, x.celular, x.direccion]
      .some((v) => (v || "").toLowerCase().includes(q));
  });

  return (
    <div className="space-y-3">
      <SectionHeader
        title="Lista de Clientes"
        subtitle={`${items.length} cliente(s) · Código: CL-X### (X = inicial del nombre)`}
        search={search} setSearch={setSearch} color="#2E6DA4"
        readOnly manageHref="/ingenieria/clientes"
      />
      {loading ? (
        <div className="bg-white rounded-2xl p-8 text-center text-gray-400">
          <Loader2 size={20} className="mx-auto animate-spin" />
        </div>
      ) : filtered.length === 0 ? (
        <div className="bg-white rounded-2xl border border-dashed border-amber-200 p-12 text-center">
          <div className="w-14 h-14 rounded-2xl mx-auto flex items-center justify-center mb-3 bg-amber-50 text-amber-500">
            <Users size={22} />
          </div>
          <h4 className="text-[14px] font-semibold text-gray-800">Sin clientes registrados</h4>
          <p className="text-[12px] text-gray-500 mt-1">Los clientes se crean y administran desde el módulo de <strong>Presupuestos</strong>.</p>
        </div>
      ) : (
        <div className="bg-white rounded-2xl border border-gray-100 shadow-sm overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full text-[13px]">
              <thead className="bg-gray-50">
                <tr className="text-left text-[11px] uppercase tracking-wider text-gray-500">
                  <th className="px-4 py-3 font-semibold">Código</th>
                  <th className="px-4 py-3 font-semibold">Cliente</th>
                  <th className="px-4 py-3 font-semibold">RUC/CI</th>
                  <th className="px-4 py-3 font-semibold">Celular</th>
                  <th className="px-4 py-3 font-semibold">Dirección</th>
                </tr>
              </thead>
              <tbody>
                {filtered.map((x) => (
                  <tr key={x._id} className="border-t border-gray-100 hover:bg-gray-50/60" data-testid={`row-${x.codigo}`}>
                    <td className="px-4 py-2.5"><CodeBadge code={x.codigo} color="#2E6DA4" /></td>
                    <td className="px-4 py-2.5 font-semibold text-gray-900">{x.cliente}</td>
                    <td className="px-4 py-2.5 text-gray-600 font-mono text-[12px]">{x.ruc_ci || "—"}</td>
                    <td className="px-4 py-2.5 text-gray-600">{x.celular || "—"}</td>
                    <td className="px-4 py-2.5 text-gray-600 truncate max-w-[200px]">{x.direccion || "—"}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
}

// ──────────────────────────────────────────────────────────
// OBRAS — Solo lectura (gestionar desde Presupuestos)
// ──────────────────────────────────────────────────────────
function ObrasSection({ onChange }) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await axios.get(`${API}/listas/obras`);
      setItems(r.data || []);
    } catch (_e) { toast.error("Error al cargar obras"); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const filtered = items.filter((x) => {
    const q = search.toLowerCase();
    return !q || [x.obra, x.codigo, x.cliente_nombre]
      .some((v) => (v || "").toLowerCase().includes(q));
  });

  return (
    <div className="space-y-3">
      <SectionHeader
        title="Lista de Obras"
        subtitle={`${items.length} obra(s) · Código: OB-<Cliente>-<N°Presup>-<Rev>-<Adic>`}
        search={search} setSearch={setSearch} color="#8B5CF6"
        readOnly
      />
      {loading ? (
        <div className="bg-white rounded-2xl p-8 text-center text-gray-400">
          <Loader2 size={20} className="mx-auto animate-spin" />
        </div>
      ) : filtered.length === 0 ? (
        <div className="bg-white rounded-2xl border border-dashed border-amber-200 p-12 text-center">
          <div className="w-14 h-14 rounded-2xl mx-auto flex items-center justify-center mb-3 bg-amber-50 text-amber-500">
            <HardHat size={22} />
          </div>
          <h4 className="text-[14px] font-semibold text-gray-800">Sin obras registradas</h4>
          <p className="text-[12px] text-gray-500 mt-1">Las obras se crean y administran desde el módulo de <strong>Presupuestos</strong>.</p>
        </div>
      ) : (
        <div className="bg-white rounded-2xl border border-gray-100 shadow-sm overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full text-[13px]">
              <thead className="bg-gray-50">
                <tr className="text-left text-[11px] uppercase tracking-wider text-gray-500">
                  <th className="px-4 py-3 font-semibold">Código</th>
                  <th className="px-4 py-3 font-semibold">Obra</th>
                  <th className="px-4 py-3 font-semibold">Cliente</th>
                  <th className="px-4 py-3 font-semibold">Dirección</th>
                </tr>
              </thead>
              <tbody>
                {filtered.map((x) => (
                  <tr key={x._id} className="border-t border-gray-100 hover:bg-gray-50/60" data-testid={`row-${x.codigo}`}>
                    <td className="px-4 py-2.5"><CodeBadge code={x.codigo} color="#8B5CF6" /></td>
                    <td className="px-4 py-2.5 font-semibold text-gray-900">{x.obra}</td>
                    <td className="px-4 py-2.5 text-gray-600">
                      <div className="flex items-center gap-1.5">
                        {x.cliente_codigo && <CodeBadge code={x.cliente_codigo} color="#2E6DA4" />}
                        <span>{x.cliente_nombre || "—"}</span>
                      </div>
                    </td>
                    <td className="px-4 py-2.5 text-gray-600 truncate max-w-[180px]">{x.direccion || "—"}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
}

// ──────────────────────────────────────────────────────────
// MATERIALES
// ──────────────────────────────────────────────────────────
function MaterialesSection({ onChange }) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [editing, setEditing] = useState(null);
  const [open, setOpen] = useState(false);
  const [showImport, setShowImport] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await axios.get(`${API}/listas/materiales`);
      setItems(r.data || []);
    } catch (_e) { toast.error("Error al cargar materiales"); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const handleSave = async (data) => {
    try {
      if (editing?._id) {
        await axios.put(`${API}/listas/materiales/${editing._id}`, data);
        toast.success("Material actualizado");
      } else {
        await axios.post(`${API}/listas/materiales`, data);
        toast.success("Material creado");
      }
      setOpen(false); setEditing(null);
      await load(); onChange?.();
    } catch (e) { toast.error(e.response?.data?.detail || "Error al guardar"); }
  };

  const handleDelete = async (id) => {
    if (!window.confirm("¿Eliminar material?")) return;
    try {
      await axios.delete(`${API}/listas/materiales/${id}`);
      toast.success("Material eliminado");
      await load(); onChange?.();
    } catch (e) { toast.error(e.response?.data?.detail || "Error"); }
  };

  const handleImportDone = async () => {
    setShowImport(false);
    await load(); onChange?.();
  };

  const filtered = items.filter((x) => {
    const q = search.toLowerCase();
    return !q || [x.descripcion, x.codigo, x.categoria, x.marca]
      .some((v) => (v || "").toLowerCase().includes(q));
  });

  const importBtn = (
    <button
      onClick={() => setShowImport(true)}
      className="flex items-center gap-1.5 px-3.5 py-2 rounded-xl text-[13px] font-semibold text-white shadow-sm hover:brightness-110 transition-all bg-[#059669]"
      data-testid="import-materiales-btn"
    >
      <Upload size={14} />
      <span>Imp. Lista</span>
    </button>
  );

  return (
    <div className="space-y-3">
      <SectionHeader
        title="Lista de Materiales"
        subtitle={`${items.length} material(es) · Código: MT-XXXX (catálogo maestro)`}
        onNew={() => { setEditing(null); setOpen(true); }}
        search={search} setSearch={setSearch} color="#10B981"
        extraButtons={importBtn}
      />
      {loading ? (
        <div className="bg-white rounded-2xl p-8 text-center text-gray-400">
          <Loader2 size={20} className="mx-auto animate-spin" />
        </div>
      ) : filtered.length === 0 ? (
        <EmptyState icon={Package} label="Materiales" color="#10B981"
          onNew={() => { setEditing(null); setOpen(true); }} />
      ) : (
        <div className="bg-white rounded-2xl border border-gray-100 shadow-sm overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full text-[13px]">
              <thead className="bg-gray-50">
                <tr className="text-left text-[11px] uppercase tracking-wider text-gray-500">
                  <th className="px-4 py-3 font-semibold">Código</th>
                  <th className="px-4 py-3 font-semibold">Descripción</th>
                  <th className="px-4 py-3 font-semibold">Categoría</th>
                  <th className="px-4 py-3 font-semibold">Marca</th>
                  <th className="px-4 py-3 font-semibold text-center">Unidad</th>
                  <th className="px-4 py-3 font-semibold text-right">Precio Ref.</th>
                  <th className="px-4 py-3 font-semibold text-right">Acciones</th>
                </tr>
              </thead>
              <tbody>
                {filtered.map((x) => (
                  <tr key={x._id} className="border-t border-gray-100 hover:bg-gray-50/60" data-testid={`row-${x.codigo}`}>
                    <td className="px-4 py-2.5"><CodeBadge code={x.codigo} color="#10B981" /></td>
                    {/* Fix AI (2026-09-04): descripción completa, en varias líneas. */}
                    <td className="px-4 py-2.5 font-semibold text-gray-900 max-w-[280px] whitespace-normal break-words" title={x.descripcion}>{x.descripcion}</td>
                    <td className="px-4 py-2.5 text-gray-600">{x.categoria || "—"}</td>
                    <td className="px-4 py-2.5 text-gray-600">{x.marca || "—"}</td>
                    <td className="px-4 py-2.5 text-center text-gray-600">{x.unidad || "un"}</td>
                    <td className="px-4 py-2.5 text-right font-mono text-[12px]">{x.precio_referencia ? formatPYG(x.precio_referencia) : "—"}</td>
                    <td className="px-4 py-2.5 text-right">
                      <div className="inline-flex items-center gap-1">
                        <button onClick={() => { setEditing(x); setOpen(true); }}
                          className="p-1.5 rounded-lg hover:bg-blue-50 text-blue-600"><Pencil size={13} /></button>
                        <button onClick={() => handleDelete(x._id)}
                          className="p-1.5 rounded-lg hover:bg-red-50 text-red-600"><Trash2 size={13} /></button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
      {open && (
        <MaterialForm initial={editing}
          onCancel={() => { setOpen(false); setEditing(null); }}
          onSave={handleSave} />
      )}
      {showImport && (
        <ImportMaterialesModal
          onClose={() => setShowImport(false)}
          onDone={handleImportDone}
        />
      )}
    </div>
  );
}

// ──────────────────────────────────────────────────────────
// IMPORT MATERIALES MODAL
// ──────────────────────────────────────────────────────────
const IMPORT_PLACEHOLDER = `Cable THW 2.5mm², Cableado General, Nexans, m, 4800
Disyuntor 1P 16A, Protecciones (Disyuntores), Schneider, un, 85000
Conduit PVC 3/4", Tuberías y Accesorios, Ditto, m, 3200
Tablero 6 polos empotrable`;

function ImportMaterialesModal({ onClose, onDone }) {
  const [text, setText] = useState("");
  const [parsed, setParsed] = useState([]);
  const [saving, setSaving] = useState(false);

  const parseText = (raw) => {
    const lines = raw.split("\n").map((l) => l.trim()).filter(Boolean);
    return lines.map((line) => {
      // Support both comma and tab as separators
      const parts = line.split(/\t|,/).map((p) => p.trim());
      return {
        descripcion: parts[0] || "",
        categoria: parts[1] || "",
        marca: parts[2] || "",
        unidad: parts[3] || "un",
        precio_referencia: parseFloat((parts[4] || "0").replace(/\./g, "").replace(",", ".")) || 0,
      };
    }).filter((r) => r.descripcion);
  };

  useEffect(() => {
    setParsed(parseText(text));
  }, [text]);

  const handleImport = async () => {
    if (!parsed.length) { toast.error("Sin ítems para importar"); return; }
    setSaving(true);
    try {
      const r = await axios.post(`${API}/listas/materiales/importar`, parsed);
      toast.success(`${r.data.importados} material(es) importado(s) correctamente`);
      onDone();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Error al importar");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal title="Importar Lista de Materiales" onClose={onClose} size="lg">
      <div className="space-y-4">
        <div className="bg-emerald-50 border border-emerald-200 rounded-xl p-3 text-[12px] text-emerald-800">
          <p className="font-semibold mb-1">Formato de importación (una fila por línea):</p>
          <p className="font-mono">Descripción, Categoría, Marca, UM, Precio</p>
          <p className="mt-1 text-emerald-700">Solo la descripción es obligatoria. El código MT-XXXX se asigna automáticamente.</p>
        </div>

        <div>
          <label className="block text-[12px] font-semibold text-gray-700 mb-1.5">
            Pegá el listado de materiales:
          </label>
          <textarea
            value={text}
            onChange={(e) => setText(e.target.value)}
            placeholder={IMPORT_PLACEHOLDER}
            rows={10}
            className="w-full border border-gray-300 rounded-xl px-3 py-2.5 text-[12px] font-mono focus:ring-2 focus:ring-emerald-500/20 focus:border-emerald-500 resize-y"
            data-testid="import-text-area"
          />
        </div>

        {parsed.length > 0 && (
          <div>
            <p className="text-[12px] font-semibold text-gray-700 mb-2">
              Vista previa — {parsed.length} ítem(s) a importar:
            </p>
            <div className="border border-gray-200 rounded-xl overflow-hidden max-h-48 overflow-y-auto">
              <table className="w-full text-[11px]">
                <thead className="bg-gray-50 sticky top-0">
                  <tr className="text-left text-[10px] uppercase text-gray-400 font-semibold">
                    <th className="px-3 py-2">Descripción</th>
                    <th className="px-3 py-2">Cat.</th>
                    <th className="px-3 py-2">Marca</th>
                    <th className="px-3 py-2 text-center">UM</th>
                    <th className="px-3 py-2 text-right">Precio</th>
                  </tr>
                </thead>
                <tbody>
                  {parsed.map((row, i) => (
                    <tr key={i} className="border-t border-gray-100">
                      <td className="px-3 py-1.5 font-medium text-gray-900 whitespace-normal break-words max-w-[220px]" title={row.descripcion}>{row.descripcion}</td>
                      <td className="px-3 py-1.5 text-gray-500">{row.categoria || "—"}</td>
                      <td className="px-3 py-1.5 text-gray-500">{row.marca || "—"}</td>
                      <td className="px-3 py-1.5 text-center text-gray-500">{row.unidad}</td>
                      <td className="px-3 py-1.5 text-right font-mono">{row.precio_referencia ? formatPYG(row.precio_referencia) : "—"}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}

        <div className="flex justify-end gap-2 pt-2">
          <button type="button" onClick={onClose} className="px-4 py-2 rounded-xl text-[13px] font-semibold text-gray-600 hover:bg-gray-100">Cancelar</button>
          <button
            onClick={handleImport}
            disabled={saving || parsed.length === 0}
            className="flex items-center gap-2 px-4 py-2 rounded-xl text-[13px] font-semibold text-white bg-[#059669] hover:brightness-110 disabled:opacity-50"
            data-testid="confirm-import-btn"
          >
            {saving ? <Loader2 size={14} className="animate-spin" /> : <Upload size={14} />}
            {saving ? "Importando..." : `Importar ${parsed.length} material(es)`}
          </button>
        </div>
      </div>
    </Modal>
  );
}

const MAT_UNITS = ["un", "m", "m²", "m³", "kg", "rollo", "caja", "par", "juego", "gl", "l"];
const MAT_CATEGORIES = [
  "Tableros Eléctricos", "Alimentadores", "Cableado General", "Iluminación",
  "Protecciones (Disyuntores)", "Ductos y Bandejas", "Tuberías y Accesorios",
  "Cajas y Accesorios", "Instrumentación", "Puesta a Tierra", "Otros",
];

function MaterialForm({ initial, onCancel, onSave }) {
  const [f, setF] = useState({
    descripcion: initial?.descripcion || "",
    unidad: initial?.unidad || "un",
    categoria: initial?.categoria || "",
    marca: initial?.marca || "",
    precio_referencia: initial?.precio_referencia || 0,
  });
  const [saving, setSaving] = useState(false);
  const [fetchingPrice, setFetchingPrice] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!f.descripcion.trim()) { toast.error("Descripción obligatoria"); return; }
    setSaving(true); await onSave(f); setSaving(false);
  };

  const fetchPrecioLC = async () => {
    if (!f.descripcion.trim()) { toast.error("Ingresá la descripción primero"); return; }
    setFetchingPrice(true);
    try {
      const r = await axios.get(`${API}/listas/materiales/precio-lc`, { params: { q: f.descripcion } });
      if (r.data.found) {
        setF((prev) => ({ ...prev, precio_referencia: r.data.precio }));
        toast.success(`Precio traído del Libro de Compras: ${formatPYG(r.data.precio)}`);
      } else {
        toast.info("No se encontró precio en el Libro de Compras para este material.");
      }
    } catch (_e) {
      toast.error("Error al buscar precio");
    } finally {
      setFetchingPrice(false);
    }
  };

  return (
    <Modal title={initial ? `Editar Material · ${initial.codigo}` : "Nuevo Material"} onClose={onCancel}>
      <form onSubmit={submit} className="space-y-3" data-testid="material-form">
        <Field label="Descripción" required>
          <input className={inputCls} value={f.descripcion}
            onChange={(e) => setF({ ...f, descripcion: e.target.value })}
            data-testid="input-material" autoFocus
            placeholder="Cable THW 2.5mm² rojo" />
        </Field>
        <div className="grid grid-cols-2 gap-3">
          <Field label="Categoría">
            <select className={inputCls} value={f.categoria}
              onChange={(e) => setF({ ...f, categoria: e.target.value })}>
              <option value="">— sin categoría —</option>
              {MAT_CATEGORIES.map((c) => <option key={c} value={c}>{c}</option>)}
            </select>
          </Field>
          <Field label="Unidad">
            <select className={inputCls} value={f.unidad}
              onChange={(e) => setF({ ...f, unidad: e.target.value })}>
              {MAT_UNITS.map((u) => <option key={u} value={u}>{u}</option>)}
            </select>
          </Field>
        </div>
        <Field label="Marca">
          <input className={inputCls} value={f.marca}
            onChange={(e) => setF({ ...f, marca: e.target.value })}
            placeholder="Schneider, Siemens..." />
        </Field>
        <div>
          <div className="flex items-center justify-between mb-1.5">
            <label className="text-[12px] font-semibold text-gray-700">Precio Referencia (PYG)</label>
            <button type="button" onClick={fetchPrecioLC} disabled={fetchingPrice}
              className="flex items-center gap-1 text-[11px] font-semibold text-emerald-700 hover:text-emerald-900 disabled:opacity-50"
              data-testid="fetch-price-btn">
              {fetchingPrice ? <Loader2 size={11} className="animate-spin" /> : <RefreshCw size={11} />}
              Traer de Libro de Compras
            </button>
          </div>
          <input type="number" min="0" step="1" className={inputCls}
            value={f.precio_referencia}
            onChange={(e) => setF({ ...f, precio_referencia: Number(e.target.value) || 0 })}
            data-testid="input-precio-ref" />
          <p className="text-[11px] text-gray-400 mt-1">Se trae el precio unitario más reciente del Libro de Compras.</p>
        </div>
        <div className="flex justify-end gap-2 pt-2">
          <button type="button" onClick={onCancel} className="px-4 py-2 rounded-xl text-[13px] font-semibold text-gray-600 hover:bg-gray-100">Cancelar</button>
          <button type="submit" disabled={saving}
            className="px-4 py-2 rounded-xl text-[13px] font-semibold text-white bg-[#10B981] hover:brightness-110 disabled:opacity-50"
            data-testid="save-material-btn">
            {saving ? <Loader2 size={14} className="animate-spin" /> : "Guardar"}
          </button>
        </div>
      </form>
    </Modal>
  );
}


// ──────────────────────────────────────────────────────────
// FRENTES — Tabla maestra "PRESUPUESTOS / FRENTES" (PDF REV COMPRAS MAT)
// 13 códigos oficiales del cliente. Editable por admin/compras.
// ──────────────────────────────────────────────────────────
function FrentesSection({ onChange }) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [editing, setEditing] = useState(null);
  const [open, setOpen] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await axios.get(`${API}/listas/frentes`);
      setItems(r.data || []);
    } catch (_e) { toast.error("Error al cargar frentes"); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const handleSave = async (data) => {
    try {
      if (editing?._id) {
        await axios.put(`${API}/listas/frentes/${editing._id}`, data);
        toast.success("Frente actualizado");
      } else {
        await axios.post(`${API}/listas/frentes`, data);
        toast.success("Frente creado");
      }
      setOpen(false); setEditing(null);
      await load(); onChange?.();
    } catch (e) { toast.error(e.response?.data?.detail || "Error al guardar"); }
  };

  const handleDelete = async (id) => {
    if (!window.confirm("¿Eliminar este presupuesto/frente?")) return;
    try {
      await axios.delete(`${API}/listas/frentes/${id}`);
      toast.success("Frente eliminado");
      await load(); onChange?.();
    } catch (e) { toast.error(e.response?.data?.detail || "Error"); }
  };

  const filtered = items.filter((x) => {
    const q = search.toLowerCase();
    return !q || [x.presupuesto, String(x.cod), (x.frentes || []).join(" ")]
      .some((v) => (v || "").toLowerCase().includes(q));
  });

  return (
    <div className="space-y-3">
      <SectionHeader
        title="Presupuestos / Frentes (PDF REV)"
        subtitle={`${items.length} código(s) · Tabla maestra del PDF "REV COMPRAS MAT" — 13 categorías oficiales`}
        onNew={() => { setEditing(null); setOpen(true); }}
        search={search} setSearch={setSearch} color="#EC4899"
      />
      {loading ? (
        <div className="bg-white rounded-2xl p-8 text-center text-gray-400">
          <Loader2 size={20} className="mx-auto animate-spin" />
        </div>
      ) : filtered.length === 0 ? (
        <EmptyState icon={Database} label="Frentes" color="#EC4899"
          onNew={() => { setEditing(null); setOpen(true); }} />
      ) : (
        <div className="bg-white rounded-2xl border border-gray-100 shadow-sm overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full text-[13px]">
              <thead className="bg-gray-50">
                <tr className="text-left text-[11px] uppercase tracking-wider text-gray-500">
                  <th className="px-4 py-3 font-semibold w-16">COD</th>
                  <th className="px-4 py-3 font-semibold">Presupuesto</th>
                  <th className="px-4 py-3 font-semibold">Frentes (sub-líneas)</th>
                  <th className="px-4 py-3 font-semibold text-right">Acciones</th>
                </tr>
              </thead>
              <tbody>
                {filtered.map((x) => (
                  <tr key={x._id} className="border-t border-gray-100 hover:bg-gray-50/60" data-testid={`row-frente-${x.cod}`}>
                    <td className="px-4 py-2.5">
                      <span className="inline-flex items-center justify-center w-8 h-8 rounded-lg bg-pink-50 text-pink-700 border border-pink-200 font-mono text-[13px] font-bold">{x.cod}</span>
                    </td>
                    <td className="px-4 py-2.5 font-semibold text-gray-900">{x.presupuesto}</td>
                    <td className="px-4 py-2.5">
                      <div className="flex flex-wrap gap-1">
                        {(x.frentes || []).map((f, i) => (
                          <span key={i} className="inline-block px-2 py-0.5 rounded-md text-[11px] bg-pink-50 text-pink-700 border border-pink-200 font-medium">{f}</span>
                        ))}
                      </div>
                    </td>
                    <td className="px-4 py-2.5 text-right">
                      <div className="inline-flex items-center gap-1">
                        <button onClick={() => { setEditing(x); setOpen(true); }}
                          className="p-1.5 rounded-lg hover:bg-blue-50 text-blue-600"><Pencil size={13} /></button>
                        <button onClick={() => handleDelete(x._id)}
                          className="p-1.5 rounded-lg hover:bg-red-50 text-red-600"><Trash2 size={13} /></button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
      {open && (
        <FrenteForm initial={editing}
          onCancel={() => { setOpen(false); setEditing(null); }}
          onSave={handleSave} />
      )}
    </div>
  );
}

function FrenteForm({ initial, onCancel, onSave }) {
  const [f, setF] = useState({
    cod: initial?.cod || "",
    presupuesto: initial?.presupuesto || "",
  });
  const [frentes, setFrentes] = useState(
    Array.isArray(initial?.frentes) && initial.frentes.length > 0 ? initial.frentes : [""]
  );
  const [saving, setSaving] = useState(false);

  const addFrente = () => setFrentes([...frentes, ""]);
  const removeFrente = (i) => setFrentes(frentes.filter((_, idx) => idx !== i));
  const updateFrente = (i, val) => setFrentes(frentes.map((s, idx) => idx === i ? val : s));

  const submit = async (e) => {
    e.preventDefault();
    const codNum = parseInt(f.cod, 10);
    if (!codNum || codNum < 1) { toast.error("COD debe ser un entero ≥ 1"); return; }
    if (!f.presupuesto.trim()) { toast.error("Nombre del presupuesto obligatorio"); return; }
    setSaving(true);
    await onSave({ cod: codNum, presupuesto: f.presupuesto.trim(), frentes: frentes.filter(Boolean) });
    setSaving(false);
  };

  return (
    <Modal title={initial ? `Editar Frente · COD ${initial.cod}` : "Nuevo Presupuesto / Frente"} onClose={onCancel} size="lg">
      <form onSubmit={submit} className="space-y-3" data-testid="frente-form">
        <div className="grid grid-cols-3 gap-3">
          <Field label="COD" required>
            <input type="number" min="1" className={inputCls} value={f.cod}
              onChange={(e) => setF({ ...f, cod: e.target.value })}
              data-testid="input-cod" autoFocus disabled={!!initial} />
          </Field>
          <div className="col-span-2">
            <Field label="Presupuesto" required>
              <input className={inputCls} value={f.presupuesto}
                onChange={(e) => setF({ ...f, presupuesto: e.target.value })}
                data-testid="input-presupuesto"
                placeholder="Ej: Alimentaciones" />
            </Field>
          </div>
        </div>
        <div>
          <div className="flex items-center justify-between mb-2">
            <label className="text-[12px] font-semibold text-gray-700 uppercase tracking-wide">Frentes (sub-líneas)</label>
            <button type="button" onClick={addFrente}
              className="flex items-center gap-1 text-[12px] font-semibold text-pink-600 hover:text-pink-700"
              data-testid="add-frente-btn">
              <Plus size={12} /> Agregar frente
            </button>
          </div>
          <div className="space-y-2">
            {frentes.map((s, i) => (
              <div key={i} className="flex items-center gap-2">
                <span className="text-[11px] text-gray-400 font-mono w-5">{i + 1}</span>
                <input className={`${inputCls} flex-1`} value={s}
                  onChange={(e) => updateFrente(i, e.target.value)}
                  placeholder="Ej: Ali. Tableros (NYY, Subt)"
                  data-testid={`frente-input-${i}`} />
                {frentes.length > 1 && (
                  <button type="button" onClick={() => removeFrente(i)}
                    className="p-1 rounded-lg hover:bg-red-50 text-red-400 hover:text-red-600">
                    <X size={13} />
                  </button>
                )}
              </div>
            ))}
          </div>
        </div>
        <div className="flex justify-end gap-2 pt-2">
          <button type="button" onClick={onCancel} className="px-4 py-2 rounded-xl text-[13px] font-semibold text-gray-600 hover:bg-gray-100">Cancelar</button>
          <button type="submit" disabled={saving}
            className="px-4 py-2 rounded-xl text-[13px] font-semibold text-white bg-[#EC4899] hover:brightness-110 disabled:opacity-50"
            data-testid="save-frente-btn">
            {saving ? <Loader2 size={14} className="animate-spin" /> : "Guardar"}
          </button>
        </div>
      </form>
    </Modal>
  );
}


// ──────────────────────────────────────────────────────────
// PRESUPUESTOS — Solo lectura (mostrar aprobados)
// ──────────────────────────────────────────────────────────
function PresupuestosSection() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await axios.get(`${API}/budgets?status=aprobado`);
      setItems(Array.isArray(r.data) ? r.data : []);
    } catch (_e) { toast.error("Error al cargar presupuestos"); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const filtered = items.filter((x) => {
    const q = search.toLowerCase();
    return !q || [x.codigo, x.cliente, x.nombre_proyecto, x.tipo]
      .some((v) => (v || "").toLowerCase().includes(q));
  });

  const fmt = (n) => {
    if (!n && n !== 0) return "—";
    return new Intl.NumberFormat("es-PY", { style: "currency", currency: "PYG", maximumFractionDigits: 0 }).format(n);
  };

  return (
    <div className="space-y-3">
      <div className="bg-white rounded-2xl border border-gray-100 shadow-sm px-5 py-4 flex flex-col sm:flex-row gap-3 items-start sm:items-center justify-between">
        <div>
          <h3 className="text-[15px] font-bold text-gray-900">Presupuestos Aprobados</h3>
          <p className="text-[12px] text-gray-500">{items.length} presupuesto(s) · Solo lectura — Gestionar desde módulo Presupuestos</p>
        </div>
        <div className="flex items-center gap-2">
          <span className="inline-flex items-center gap-1.5 text-[11px] font-semibold bg-amber-50 text-amber-700 border border-amber-200 rounded-full px-3 py-1">
            <Lock size={10} /> Solo lectura
          </span>
          <div className="relative">
            <Search size={13} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-gray-400" />
            <input
              type="text" placeholder="Buscar..." value={search}
              onChange={e => setSearch(e.target.value)}
              className="pl-7 pr-3 py-1.5 rounded-lg border border-gray-200 text-[12px] w-44 focus:ring-1 focus:ring-[#6366F1]/30 focus:border-[#6366F1]"
              data-testid="search-presupuestos"
            />
          </div>
        </div>
      </div>

      {loading ? (
        <div className="bg-white rounded-2xl p-8 text-center text-gray-400">
          <Loader2 size={20} className="mx-auto animate-spin" />
        </div>
      ) : filtered.length === 0 ? (
        <div className="bg-white rounded-2xl border border-dashed border-indigo-200 p-12 text-center">
          <div className="w-14 h-14 rounded-2xl mx-auto flex items-center justify-center mb-3 bg-indigo-50 text-indigo-500">
            <FileText size={22} />
          </div>
          <h4 className="text-[14px] font-semibold text-gray-800">Sin presupuestos aprobados</h4>
          <p className="text-[12px] text-gray-500 mt-1">Los presupuestos se aprueban desde el módulo de <strong>Presupuestos</strong>.</p>
        </div>
      ) : (
        <div className="bg-white rounded-2xl border border-gray-100 shadow-sm overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full text-[13px]">
              <thead className="bg-gray-50">
                <tr className="text-left text-[11px] uppercase tracking-wider text-gray-500">
                  <th className="px-4 py-3 font-semibold">Código</th>
                  <th className="px-4 py-3 font-semibold">Cliente</th>
                  <th className="px-4 py-3 font-semibold">Proyecto</th>
                  <th className="px-4 py-3 font-semibold">Tipo</th>
                  <th className="px-4 py-3 font-semibold text-right">Total</th>
                  <th className="px-4 py-3 font-semibold">Fecha</th>
                </tr>
              </thead>
              <tbody>
                {filtered.map((x) => (
                  <tr key={x._id || x.id} className="border-t border-gray-100 hover:bg-gray-50/60" data-testid={`ppto-row-${x._id || x.id}`}>
                    <td className="px-4 py-2.5">
                      <span className="font-mono text-[11px] bg-indigo-50 text-indigo-700 px-2 py-0.5 rounded-full border border-indigo-100">
                        {x.codigo || "—"}
                      </span>
                    </td>
                    <td className="px-4 py-2.5 font-semibold text-gray-900">{x.cliente || "—"}</td>
                    <td className="px-4 py-2.5 text-gray-600 truncate max-w-[200px]">{x.nombre_proyecto || x.project_name || "—"}</td>
                    <td className="px-4 py-2.5">
                      <span className="text-[11px] bg-gray-100 text-gray-600 px-2 py-0.5 rounded-full">{x.tipo || "Material"}</span>
                    </td>
                    <td className="px-4 py-2.5 text-right font-mono font-semibold text-gray-900">{fmt(x.total)}</td>
                    <td className="px-4 py-2.5 text-gray-500 text-[12px]">
                      {x.created_at ? new Date(x.created_at).toLocaleDateString("es-PY", { day: "2-digit", month: "2-digit", year: "2-digit" }) : "—"}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
}
